/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { FieldValues, useForm } from "react-hook-form";
import { Else, If, Then } from "react-if";
import { Link } from "react-router-dom";
import { Button, FlexContainer, Loader, theme } from "@nordcloud/gnui";
import {
  Account,
  CloudProviderType,
  Provider,
  useAccountV2Query,
  useCreateLinkMutation,
  useDeleteLinkMutation,
  useUpdateLinkMutation,
} from "~/generated/graphql";
import { CloudResourceInput } from "~/components";
import { CloudResource } from "~/components/EstateRecordsList";
import { ERROR_TEXT } from "~/constants";
import { showError, showSuccess } from "~/services/toast";
import { generateActionSuccessText, isNil, isNotNil } from "~/tools";
import { LinkEditButton } from "./LinkEditButton";

type AccountMetadata = {
  sourceCloudProviderId: string | undefined;
};

type AccountV2 = Pick<Account, "metadata" | "provider" | "providerType"> | null;

type Props = {
  nordcloudId: string;
  cloudAccountId?: string;
  cloudResource?: Pick<CloudResource, "id" | "name">;
  onChange: () => void;
  providerTypes: string[];
};

export function InlineLink({
  cloudAccountId,
  nordcloudId,
  cloudResource,
  onChange,
  providerTypes,
}: Props) {
  const { register, handleSubmit, setValue, reset } = useForm();
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [target, setTarget] = React.useState<CloudResource>();
  const [updating, setUpdating] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);

  // get the account of the node
  const { data: cloudAccount, loading: accountLoading } = useAccountV2Query({
    variables: { id: cloudAccountId ?? nordcloudId },
  });

  const [createLink] = useCreateLinkMutation();
  const [updateLink] = useUpdateLinkMutation();
  const [deleteLink] = useDeleteLinkMutation();

  const refreshParentAndShowMessage = (message: string) => {
    Promise.resolve(onChange()).then(() => {
      setUpdating(false);
      showSuccess(message);
      return true;
    });
  };

  const onEdit = async (values: FieldValues) => {
    closeEditMode();
    try {
      setUpdating(true);
      const newTargetVmId = values.id;

      if (cloudResource) {
        await updateLink({
          variables: {
            oldLink: {
              targetNid: nordcloudId,
              sourceNid: cloudResource.id,
            },
            newLink: {
              targetNid: nordcloudId,
              sourceNid: newTargetVmId,
            },
          },
        });
      } else {
        await createLink({
          variables: {
            targetNid: nordcloudId,
            sourceNid: newTargetVmId,
          },
        });
      }

      refreshParentAndShowMessage(
        generateActionSuccessText(`Assignment`)()(
          `${cloudResource ? "updated" : "created"}`
        )()
      );
    } catch (error) {
      setUpdating(false);
      showError(error.message ?? ERROR_TEXT.default);
    }
  };

  const onDelete = async () => {
    if (!cloudResource) {
      showError("There is no assignment");
      return;
    }
    closeEditMode();
    try {
      setUpdating(true);
      await deleteLink({
        variables: {
          targetNid: nordcloudId,
          sourceNid: cloudResource.id,
        },
      });
      refreshParentAndShowMessage(
        generateActionSuccessText(`Assignment`)()("deleted")()
      );
    } catch (error) {
      setUpdating(false);
      showError(error.message ?? ERROR_TEXT.default);
    }
  };

  const closeEditMode = () => {
    reset();
    setIsEditMode(false);
    setIsDirty(false);
    setTarget(undefined);
  };

  const openEditMode = () => {
    if (cloudResource) {
      setTarget(cloudResource as CloudResource);
    }
    setIsEditMode(true);
  };

  const EditModeForm = (
    <form css={{ display: "block", width: "100%" }}>
      <input
        type="hidden"
        {...register("id", { required: true })}
        defaultValue={target?.id ?? ""}
      />
      <FlexContainer>
        <CloudResourceInput
          small
          value={target}
          searchFilters={getSearchFilters(
            providerTypes,
            cloudAccount?.accountV2
          )}
          onChange={(resource: CloudResource) => {
            setTarget(resource);
            setValue("id", resource.id);
            setIsDirty(true);
          }}
        />
        <If condition={isDirty || !cloudResource}>
          <Then>
            <Button
              ml={theme.spacing.spacing02}
              type="button"
              icon="save"
              size="md"
              severity="low"
              disabled={!isDirty}
              onClick={handleSubmit(onEdit)}
            >
              Save
            </Button>
          </Then>
          <Else>
            <Button
              ml={theme.spacing.spacing02}
              type="button"
              icon="trash"
              size="md"
              severity="low"
              onClick={handleSubmit(onDelete)}
            >
              Delete
            </Button>
          </Else>
        </If>
        <Button
          type="button"
          ml={theme.spacing.spacing02}
          icon="close"
          size="md"
          severity="low"
          onClick={closeEditMode}
        >
          Cancel
        </Button>
      </FlexContainer>
    </form>
  );

  const isLoading = accountLoading || updating;

  if (isLoading) {
    return <Loader inContent />;
  }

  if (isEditMode) {
    return EditModeForm;
  }

  return (
    <FlexContainer alignItems="center">
      {cloudResource && (
        <Link
          data-testid={`link-${cloudResource.id}`}
          to={`/estate/${cloudResource.id}`}
        >
          {cloudResource.name}
        </Link>
      )}
      <LinkEditButton onClick={openEditMode} />
    </FlexContainer>
  );
}

function getSearchFilters(typeSubtype: string[], cloudAccount?: AccountV2) {
  const isAccountNotHybrid = cloudAccount?.provider !== Provider.Hybrid;
  const isAccountKubernetesOrOpenshift = isKubernetesOrOpenshift(
    cloudAccount?.providerType ?? undefined
  );
  const isDiscoveredKubernetesOrOpenshift =
    isAccountKubernetesOrOpenshift && isAccountNotHybrid;

  if (isDiscoveredKubernetesOrOpenshift) {
    return {
      ...getAccountFilter(cloudAccount?.metadata),
      typeSubtype,
    };
  }

  return {
    typeSubtype,
  };
}

function isKubernetesOrOpenshift(
  type: CloudProviderType = CloudProviderType.Cloud
) {
  const kubernetesOpenshiftTypes = [
    CloudProviderType.Kubernetes,
    CloudProviderType.Openshift,
  ];

  return kubernetesOpenshiftTypes.includes(type);
}

function getAccountFilter(metadata?: string | null) {
  if (isNil(metadata)) {
    return {};
  }

  const accountMetadata = getAccountMetadata(metadata);

  if (isNil(accountMetadata)) {
    return {};
  }

  const { sourceCloudProviderId } = accountMetadata;

  const hasCloudProviderId = isNotNil(sourceCloudProviderId);

  if (hasCloudProviderId) {
    return {
      account: [sourceCloudProviderId],
    };
  }

  return {};
}

function getAccountMetadata(metadata: string): AccountMetadata | undefined {
  try {
    return JSON.parse(metadata) as AccountMetadata;
  } catch {
    showError("Failed to parse account metadata");
  }
}
